import { useState } from 'react';
import { 
  HelpCircle, 
  ChevronDown, 
  ChevronRight,
  Target,
  Shield,
  Filter,
  Zap,
  Crown,
  TrendingUp,
  DollarSign,
  Clock,
  AlertTriangle,
  CheckCircle
} from 'lucide-react';

const sections = [
  {
    id: 'manual-trading',
    title: 'Manual Trading (Markets Page)',
    icon: DollarSign,
    subsections: [
      {
        title: 'Trading on Markets',
        content: [
          { setting: 'Market Orders', default: '', desc: 'Buy or sell at best available price. Uses slippage tolerance to protect against price movement. Fastest execution.' },
          { setting: 'Limit Orders', default: '', desc: 'Set your exact price. Order sits in the book until filled or cancelled. Good for patient traders.' },
          { setting: 'Slippage', default: '2%', desc: 'Maximum price movement allowed for market orders. Higher = more likely to fill, but worse price.' },
        ]
      },
      {
        title: 'Goggles Mode (Opportunity Scanner)',
        content: [
          { setting: 'What It Does', default: '', desc: 'Highlights markets with good trading opportunities based on price, liquidity, volume, and timing.' },
          { setting: 'Edge Zone (20-40% or 60-80%)', default: '+25 pts', desc: 'Best risk/reward. Not too certain, not a coin flip.' },
          { setting: 'Deep Liquidity (>$50k)', default: '+25 pts', desc: 'Can execute large trades without moving price.' },
          { setting: 'High Volume (>$100k/day)', default: '+20 pts', desc: 'Active market with real trading interest.' },
          { setting: 'Expires Soon (<4 hours)', default: '+20 pts', desc: 'Short-term actionable opportunity.' },
          { setting: 'Tight Spread (<2%)', default: '+10 pts', desc: 'Efficient pricing, fair market.' },
          { setting: 'Hot (70+ pts)', default: '', desc: 'Strong opportunity - multiple positive factors.' },
          { setting: 'Warm (50-70 pts)', default: '', desc: 'Moderate opportunity - worth considering.' },
        ]
      },
      {
        title: 'Order Types',
        content: [
          { setting: 'MARKET', default: 'Recommended', desc: 'GTC order with aggressive 5% slippage. Best fill rate, sits in book if not immediately filled.' },
          { setting: 'FOK (Fill-Or-Kill)', default: '', desc: 'Must fill 100% immediately or cancel. Use when you need exact position size.' },
          { setting: 'FAK (Fill-And-Kill)', default: '', desc: 'Fill what you can immediately, cancel the rest. Good balance of speed and partial fills.' },
          { setting: 'GTC (Good-Til-Cancelled)', default: '', desc: 'Order stays in book until filled or you cancel. For patient limit orders.' },
        ]
      }
    ]
  },
  {
    id: 'copy-trading',
    title: 'Copy Trading Settings',
    icon: Target,
    subsections: [
      {
        title: 'Position Sizing',
        content: [
          { setting: 'Mode', default: 'Fixed', desc: 'How to calculate trade size: Fixed (flat dollar amount), Percentage (% of trader\'s position), or Proportional (% of your balance)' },
          { setting: 'Fixed Amount', default: '$10', desc: 'Dollar amount per trade when using fixed mode' },
          { setting: '% of Trader', default: '10%', desc: 'Copy this percentage of the trader\'s position size. If trader buys $500, you buy $50.' },
          { setting: '% of Balance', default: '5%', desc: 'Use this percentage of your USDC balance per trade' },
          { setting: 'Max Position Size', default: '$100', desc: 'Maximum dollars allowed in any single position' },
          { setting: 'Min Position Size', default: '$1', desc: 'Minimum trade size - trades below this are skipped' },
        ]
      },
      {
        title: 'Risk Management',
        content: [
          { setting: 'Stop Loss', default: '25%', desc: 'Automatically sell if position drops this much from entry. Protects against big losses.' },
          { setting: 'Take Profit', default: '50%', desc: 'Automatically sell when position gains this much. Locks in profits.' },
          { setting: 'Auto-Sell Before Expiry', default: 'Off', desc: 'Automatically sell positions X minutes before market expires. Helps recover from losing positions before resolution locks in the loss.' },
          { setting: 'Max Exposure', default: '$500', desc: 'Maximum total dollars invested with this trader across all positions' },
          { setting: 'Max Open Positions', default: '10', desc: 'Maximum concurrent positions from this trader. New trades queue until positions close.' },
          { setting: 'Daily Loss Limit', default: '$100', desc: 'Stop copying after losing this much in a day. Resets at midnight UTC.' },
        ]
      },
      {
        title: 'Trade Filters',
        content: [
          { setting: 'Copy Buys', default: 'Yes', desc: 'Copy when trader buys shares' },
          { setting: 'Copy Sells', default: 'Yes', desc: 'Copy when trader sells shares' },
          { setting: 'Min Trader Trade Amount', default: '$0', desc: 'Only copy if the trader\'s trade is >= this dollar amount. Set to $500 to only follow big trades. 0 = copy all trades.' },
          { setting: 'Min Price', default: '5¢', desc: 'Only copy trades where share price > 5¢. Avoids extreme longshots.' },
          { setting: 'Max Price', default: '95¢', desc: 'Only copy trades where share price < 95¢. Avoids near-certainties with no upside.' },
          { setting: 'Min Liquidity', default: '$1,000', desc: 'Skip markets with less than this liquidity. Low liquidity = hard to exit.' },
        ]
      },
      {
        title: 'Execution',
        content: [
          { setting: 'Order Type', default: 'FAK', desc: 'FAK (Fill-And-Kill) allows partial fills. FOK (Fill-Or-Kill) requires 100% fill or cancels. FAK recommended.' },
          { setting: 'Price Tolerance', default: '2¢', desc: 'Accept prices within 2 cents of trader\'s price' },
          { setting: 'Retry on Fail', default: 'Yes', desc: 'Retry failed orders up to 3 times with 2 second delays' },
        ]
      }
    ]
  },
  {
    id: 'whale-vault',
    title: 'Whale Vault Settings',
    icon: Crown,
    badge: 'Whale Tier',
    subsections: [
      {
        title: 'Trading Mode',
        content: [
          { setting: 'Paper Mode', default: 'Default', desc: 'Simulates trades without real money. Use to test strategies before going live.' },
          { setting: 'Real Mode', default: '', desc: '⚠️ Executes actual trades on Polymarket with your connected wallet. Real money at risk.' },
        ]
      },
      {
        title: 'Market Selection',
        content: [
          { setting: 'Category', default: 'All', desc: '"All" trades any market. "Crypto" only trades crypto prediction markets. "Sports" only trades game markets.' },
          { setting: 'Min Hours to Expiry', default: '0.25 (15 min)', desc: 'Skip markets expiring sooner than this. Low values catch 15-min crypto markets.' },
          { setting: 'Max Hours to Expiry', default: '48 hours', desc: 'Skip markets expiring later than this' },
          { setting: 'Max Markets Per Tick', default: '30', desc: 'Maximum markets to evaluate each engine cycle' },
        ]
      },
      {
        title: 'Risk Settings',
        content: [
          { setting: 'Max Open Positions', default: '8', desc: 'Maximum concurrent Whale Vault positions' },
          { setting: 'Base Trade Amount', default: '$250', desc: 'Starting trade size for each position' },
          { setting: 'Max Trade Amount', default: '$1,500', desc: 'Maximum single trade size' },
          { setting: 'Max Daily Loss', default: '$750', desc: 'Auto-stops engine if daily losses exceed this. Resets at midnight UTC.' },
          { setting: 'Cooldown', default: '120 sec', desc: 'Wait time between trades on same market' },
        ]
      },
      {
        title: 'Stop Loss & Take Profit',
        content: [
          { setting: 'Stop Loss %', default: '15%', desc: 'Auto-sell if position drops by this percentage from entry price.' },
          { setting: 'Trailing Stop Loss', default: 'Off', desc: 'When enabled, stop loss trails the highest price reached. Locks in gains as position moves in your favor.' },
          { setting: 'Take Profit %', default: '25%', desc: 'Auto-sell when position gains by this percentage.' },
          { setting: 'Break-Even Stop', default: 'Off', desc: 'After position gains X%, moves stop loss to entry price. Guarantees no loss after trigger.' },
          { setting: 'Break-Even Trigger', default: '10%', desc: 'Profit percentage required to activate break-even stop.' },
        ]
      },
      {
        title: 'Signal Settings',
        content: [
          { setting: 'Signal Mode', default: 'Microstructure', desc: 'Microstructure: analyzes momentum and price velocity. TA: uses RSI/EMA from CoinGecko data.' },
          { setting: 'Threshold', default: '0.52', desc: 'Minimum signal strength (0.5 = neutral). Higher = fewer but higher-confidence trades.' },
          { setting: 'Lookback Points', default: '24', desc: 'Number of price samples for momentum calculation' },
        ]
      },
      {
        title: 'Execution Settings',
        content: [
          { setting: 'Order Type', default: 'MARKET', desc: 'MARKET (aggressive slippage, best fills), FOK (all-or-nothing), FAK (partial fills OK), GTC (limit order)' },
          { setting: 'Max Slippage', default: '5%', desc: 'Maximum price slippage allowed on market orders' },
        ]
      },
      {
        title: 'Filters',
        content: [
          { setting: 'Min Liquidity', default: '$5,000', desc: 'Skip markets with less liquidity' },
          { setting: 'Max Spread', default: '6%', desc: 'Skip markets where bid-ask spread > 6%' },
          { setting: 'Min 24h Volume', default: '$1,000', desc: 'Skip low-activity markets' },
        ]
      }
    ]
  },
  {
    id: 'wallet-settings',
    title: 'Wallet & Account Settings',
    icon: Shield,
    subsections: [
      {
        title: 'Wallet Connection',
        content: [
          { setting: 'Private Key', default: '', desc: 'Your Polygon wallet private key. Encrypted and stored securely. Required for real trading.' },
          { setting: 'Proxy Address', default: '', desc: 'For Polymarket users: your trading proxy address. Leave blank if using direct wallet.' },
          { setting: 'Signature Type', default: '0 (EOA)', desc: 'Type 0 = MetaMask/Phantom (most users). Type 1 = Magic/Email. Type 2 = Polymarket proxy/safe wallet.' },
        ]
      },
      {
        title: 'Signature Types Explained',
        content: [
          { setting: 'Type 0 (EOA)', default: 'Most common', desc: 'Standard wallet like MetaMask, Phantom, Rabby. Signs transactions directly.' },
          { setting: 'Type 1 (Magic)', default: '', desc: 'Email-based wallets using Magic.link authentication.' },
          { setting: 'Type 2 (Proxy)', default: '', desc: 'Polymarket-created proxy wallets. Used if you deposit directly through Polymarket.' },
        ]
      },
      {
        title: 'Common Wallet Issues',
        content: [
          { setting: 'Wrong Signature Type', default: '', desc: 'Causes "invalid signature" errors. MetaMask users must use Type 0, not Type 2.' },
          { setting: 'Missing Proxy Address', default: '', desc: 'If using Polymarket proxy, you must enter both your EOA address AND the proxy address.' },
          { setting: 'USDC Balance', default: '', desc: 'Ensure your wallet has USDC on Polygon. Gas fees are paid in MATIC but trades use USDC.' },
        ]
      }
    ]
  },
  {
    id: 'arbitrage',
    title: 'Arbitrage Settings',
    icon: Zap,
    badge: 'Whale Tier • Paper Only',
    subsections: [
      {
        title: 'How Arbitrage Works',
        content: [
          { setting: 'Strategy', default: '', desc: 'Buy YES when cheap, buy NO when cheap. Keep combined cost below $1.00 for guaranteed profit when market resolves.' },
          { setting: 'Example', default: '', desc: 'Buy YES at 45¢ + NO at 50¢ = 95¢ total. One side pays $1.00 at resolution = 5¢ guaranteed profit per pair.' },
        ]
      },
      {
        title: 'Arbitrage Parameters',
        content: [
          { setting: 'Target Pair Cost', default: '96¢', desc: 'Aim for YES + NO average cost of 96¢ (4% profit margin)' },
          { setting: 'Max Pair Cost', default: '98¢', desc: 'Never let combined cost exceed 98¢ (safety margin)' },
          { setting: 'Min Discount Threshold', default: '48¢', desc: 'Only buy if price < 48¢ (looking for discounts)' },
          { setting: 'Max Imbalance Ratio', default: '1.5:1', desc: 'Keep YES and NO quantities balanced' },
        ]
      },
      {
        title: 'Risk Settings',
        content: [
          { setting: 'Max Capital Per Market', default: '$500', desc: 'Maximum investment in any single market' },
          { setting: 'Max Total Capital', default: '$2,000', desc: 'Maximum across all arbitrage positions' },
          { setting: 'Max Open Positions', default: '5', desc: 'Maximum concurrent arbitrage markets' },
        ]
      }
    ]
  }
];

const troubleshooting = [
  {
    issue: '"Max Exposure Reached"',
    icon: AlertTriangle,
    solution: 'Your current positions with this trader exceed your Max Exposure limit. Increase Max Exposure in settings, wait for positions to close, or manually close some positions.'
  },
  {
    issue: '"Stop Loss Failed" / "Take Profit Failed"',
    icon: AlertTriangle,
    solution: 'The sell order couldn\'t execute. Common causes: insufficient USDC balance, low market liquidity, or wallet allowance needs refresh. System retries up to 5 times with 60-second cooldowns.'
  },
  {
    issue: '"Max Open Positions Reached"',
    icon: AlertTriangle,
    solution: 'You\'ve hit your position limit. New trades are queued until existing positions close.'
  },
  {
    issue: 'Whale Vault Shows "No Signal"',
    icon: CheckCircle,
    solution: 'This is normal. The engine evaluated markets but none met the signal threshold. It\'s waiting for high-confidence opportunities.'
  },
  {
    issue: 'Whale Vault Stops Automatically',
    icon: AlertTriangle,
    solution: 'Possible reasons: Daily Loss Limit hit (check your P&L), subscription expired, or no eligible markets match your filters.'
  },
  {
    issue: '"Market not found" on Markets page',
    icon: AlertTriangle,
    solution: 'The market may have expired, been resolved, or is not tradeable. Try refreshing the page or searching for a different market.'
  },
  {
    issue: '"Invalid signature" errors',
    icon: AlertTriangle,
    solution: 'Check your Signature Type in Settings > Wallet. MetaMask/Phantom users need Type 0. Users with Polymarket proxy wallets need Type 2.'
  },
  {
    issue: 'Orders not filling',
    icon: AlertTriangle,
    solution: 'Try increasing slippage tolerance or switching to MARKET order type. Low liquidity markets may require higher slippage (5-10%).'
  },
  {
    issue: 'Trailing Stop not working',
    icon: CheckCircle,
    solution: 'Trailing stop only activates after position goes into profit. It tracks the highest price reached and triggers when price falls by the SL percentage from that high.'
  }
];

function AccordionSection({ section, isOpen, onToggle }) {
  const Icon = section.icon;
  
  return (
    <div className="border border-dark-700 rounded-xl overflow-hidden">
      <button
        onClick={onToggle}
        className="w-full flex items-center justify-between p-4 bg-dark-800 hover:bg-dark-700 transition-colors"
      >
        <div className="flex items-center gap-3">
          <div className="p-2 rounded-lg bg-primary-500/20">
            <Icon className="w-5 h-5 text-primary-400" />
          </div>
          <span className="font-semibold text-white">{section.title}</span>
          {section.badge && (
            <span className="px-2 py-0.5 text-xs rounded-full bg-accent-500/20 text-accent-400 border border-accent-500/30">
              {section.badge}
            </span>
          )}
        </div>
        {isOpen ? (
          <ChevronDown className="w-5 h-5 text-gray-400" />
        ) : (
          <ChevronRight className="w-5 h-5 text-gray-400" />
        )}
      </button>
      
      {isOpen && (
        <div className="p-4 bg-dark-900 space-y-6">
          {section.subsections.map((sub, idx) => (
            <div key={idx}>
              <h4 className="text-sm font-semibold text-primary-400 uppercase tracking-wide mb-3">
                {sub.title}
              </h4>
              <div className="space-y-3">
                {sub.content.map((item, i) => (
                  <div key={i} className="flex gap-4 p-3 rounded-lg bg-dark-800/50">
                    <div className="flex-shrink-0 w-32">
                      <span className="text-sm font-medium text-white">{item.setting}</span>
                      {item.default && (
                        <div className="text-xs text-gray-500 mt-0.5">Default: {item.default}</div>
                      )}
                    </div>
                    <p className="text-sm text-gray-400 flex-1">{item.desc}</p>
                  </div>
                ))}
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}

export default function Help() {
  const [openSections, setOpenSections] = useState(['copy-trading']);

  const toggleSection = (id) => {
    setOpenSections(prev => 
      prev.includes(id) 
        ? prev.filter(s => s !== id)
        : [...prev, id]
    );
  };

  return (
    <div className="max-w-4xl mx-auto space-y-8">
      {/* Header */}
      <div className="text-center">
        <div className="inline-flex items-center justify-center p-3 rounded-2xl bg-primary-500/20 mb-4">
          <HelpCircle className="w-8 h-8 text-primary-400" />
        </div>
        <h1 className="text-3xl font-bold text-white mb-2">Settings Guide</h1>
        <p className="text-gray-400">
          Complete guide to all BetMimic settings and what they mean
        </p>
      </div>

      {/* Quick Tips */}
      <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
        <div className="p-4 rounded-xl bg-gradient-to-br from-green-500/10 to-green-600/5 border border-green-500/20">
          <Shield className="w-6 h-6 text-green-400 mb-2" />
          <h3 className="font-semibold text-white mb-1">Start Conservative</h3>
          <p className="text-sm text-gray-400">Use small fixed amounts and tight stop losses while learning.</p>
        </div>
        <div className="p-4 rounded-xl bg-gradient-to-br from-blue-500/10 to-blue-600/5 border border-blue-500/20">
          <Filter className="w-6 h-6 text-blue-400 mb-2" />
          <h3 className="font-semibold text-white mb-1">Filter Wisely</h3>
          <p className="text-sm text-gray-400">5-95¢ price range captures best risk/reward opportunities.</p>
        </div>
        <div className="p-4 rounded-xl bg-gradient-to-br from-purple-500/10 to-purple-600/5 border border-purple-500/20">
          <TrendingUp className="w-6 h-6 text-purple-400 mb-2" />
          <h3 className="font-semibold text-white mb-1">Max Exposure Matters</h3>
          <p className="text-sm text-gray-400">Limits total risk per trader. Don't put all eggs in one basket.</p>
        </div>
      </div>

      {/* Settings Sections */}
      <div className="space-y-4">
        <h2 className="text-xl font-bold text-white">All Settings Explained</h2>
        {sections.map(section => (
          <AccordionSection
            key={section.id}
            section={section}
            isOpen={openSections.includes(section.id)}
            onToggle={() => toggleSection(section.id)}
          />
        ))}
      </div>

      {/* Troubleshooting */}
      <div className="space-y-4">
        <h2 className="text-xl font-bold text-white">Common Issues & Solutions</h2>
        <div className="space-y-3">
          {troubleshooting.map((item, idx) => {
            const Icon = item.icon;
            return (
              <div key={idx} className="p-4 rounded-xl bg-dark-800 border border-dark-700">
                <div className="flex items-start gap-3">
                  <Icon className={`w-5 h-5 mt-0.5 flex-shrink-0 ${item.icon === CheckCircle ? 'text-green-400' : 'text-yellow-400'}`} />
                  <div>
                    <h4 className="font-semibold text-white mb-1">{item.issue}</h4>
                    <p className="text-sm text-gray-400">{item.solution}</p>
                  </div>
                </div>
              </div>
            );
          })}
        </div>
      </div>

      {/* Order Types Explanation */}
      <div className="p-6 rounded-xl bg-dark-800 border border-dark-700">
        <h3 className="font-bold text-white mb-4 flex items-center gap-2">
          <Zap className="w-5 h-5 text-primary-400" />
          Order Types Explained
        </h3>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          <div className="p-4 rounded-lg bg-dark-900">
            <div className="font-semibold text-green-400 mb-1">FAK (Fill-And-Kill)</div>
            <p className="text-sm text-gray-400">
              Fills as much as possible at your price, cancels the rest. 
              <span className="text-green-400"> Recommended for most users.</span>
            </p>
          </div>
          <div className="p-4 rounded-lg bg-dark-900">
            <div className="font-semibold text-blue-400 mb-1">FOK (Fill-Or-Kill)</div>
            <p className="text-sm text-gray-400">
              Must fill 100% of your order or cancels entirely. 
              Use when you need exact position sizes.
            </p>
          </div>
        </div>
      </div>

      {/* Glossary */}
      <div className="p-6 rounded-xl bg-dark-800 border border-dark-700">
        <h3 className="font-bold text-white mb-4">Glossary</h3>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-3 text-sm">
          <div><span className="text-primary-400 font-medium">Liquidity:</span> <span className="text-gray-400">Total dollars available in the order book</span></div>
          <div><span className="text-primary-400 font-medium">Spread:</span> <span className="text-gray-400">Difference between best bid and ask prices</span></div>
          <div><span className="text-primary-400 font-medium">NegRisk:</span> <span className="text-gray-400">Multi-outcome markets (like elections)</span></div>
          <div><span className="text-primary-400 font-medium">Microstructure:</span> <span className="text-gray-400">Analysis of order flow and book imbalances</span></div>
          <div><span className="text-primary-400 font-medium">Confluence:</span> <span className="text-gray-400">Multiple indicators agreeing on direction</span></div>
          <div><span className="text-primary-400 font-medium">Pair Cost:</span> <span className="text-gray-400">Combined average price of YES + NO shares</span></div>
        </div>
      </div>
    </div>
  );
}
